import { showToast } from "../utils/toast"

// The Phone column's status menu: one menu for the whole grid, placed under the clicked cell.
// A pick saves the row's phone status and swaps its cell; open call tasks are left alone.
export class PhoneStatusMenu {
  private readonly menu: HTMLElement
  private trigger: HTMLElement | null = null
  private saving = false

  constructor(menu: HTMLElement) {
    this.menu = menu
    this.menu.addEventListener("click", this.onPick)
    document.addEventListener("click", this.onOutside, true)
    document.addEventListener("keydown", this.onKey)
    window.addEventListener("scroll", this.close, true)
    window.addEventListener("resize", this.close)
  }

  destroy(): void {
    this.menu.removeEventListener("click", this.onPick)
    document.removeEventListener("click", this.onOutside, true)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("scroll", this.close, true)
    window.removeEventListener("resize", this.close)
  }

  toggle(trigger: HTMLElement): void {
    if (this.trigger === trigger && !this.menu.hidden) {
      this.close()
      return
    }
    this.open(trigger)
  }

  readonly close = (): void => {
    if (this.menu.hidden) return
    this.menu.hidden = true
    this.trigger?.setAttribute("aria-expanded", "false")
    this.trigger = null
  }

  private open(trigger: HTMLElement): void {
    this.close()
    this.trigger = trigger
    const current = trigger.dataset.phoneStatus || ""
    this.menu.querySelectorAll<HTMLElement>(".psm-opt").forEach(opt => {
      const on = opt.dataset.value === current
      opt.classList.toggle("current", on)
      opt.setAttribute("aria-checked", String(on))
    })

    this.menu.hidden = false
    trigger.setAttribute("aria-expanded", "true")
    this.place(trigger)
  }

  // Under the cell, or above it when the viewport has no room below.
  private place(trigger: HTMLElement): void {
    const cell = trigger.getBoundingClientRect()
    const menu = this.menu.getBoundingClientRect()
    const below = cell.bottom + 6
    const top = below + menu.height > window.innerHeight - 8 ? Math.max(8, cell.top - menu.height - 6) : below
    const left = Math.min(cell.left, window.innerWidth - menu.width - 8)
    this.menu.style.top = `${top}px`
    this.menu.style.left = `${Math.max(8, left)}px`
  }

  private readonly onOutside = (event: Event): void => {
    const target = event.target as Node
    if (this.menu.hidden || this.menu.contains(target) || this.trigger?.contains(target)) return
    this.close()
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || this.menu.hidden) return
    const trigger = this.trigger
    this.close()
    trigger?.focus()
  }

  private readonly onPick = (event: Event): void => {
    const opt = (event.target as HTMLElement).closest<HTMLElement>(".psm-opt")
    const trigger = this.trigger
    if (!opt || !trigger || this.saving) return

    const row = trigger.closest<HTMLTableRowElement>("tr[data-bp-id]")
    const status = opt.dataset.value || ""
    this.close()
    if (!row || status === (trigger.dataset.phoneStatus || "")) return

    this.save(row, status)
  }

  private async save(row: HTMLTableRowElement, status: string): Promise<void> {
    this.saving = true
    try {
      const response = await fetch(this.menu.dataset.url || "", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ bots_prospect_id: row.dataset.bpId, status })
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.ok) throw new Error(data?.error || `HTTP ${response.status}`)

      row.dataset.phone = data.status_calling || ""
      const cell = row.querySelector(".st-phone")
      if (cell && data.cell) cell.outerHTML = data.cell
    } catch (_e) {
      showToast("Couldn't update the phone status", "error")
    } finally {
      this.saving = false
    }
  }
}
